/**
 * Greedy first-fit row packing for recommendation chips.
 *
 * Pagination by fixed count leaves pages ragged — "6 per page" might fill
 * two rows and a stub at one width and overflow at another. Instead, pack
 * by measurement: given each chip's rendered width and the container width,
 * fill rows greedily and cut a page every `rowsPerPage` rows. Every page is
 * exactly as full as the container allows.
 *
 * The input order is the PRIORITY order — the app pre-sorts (pinned
 * favorites first, then short-first). At each slot the earliest remaining
 * chip that fits the row's leftover width is taken: a favorite is grabbed
 * the moment it fits, and when it doesn't, a later chip back-fills the gap
 * to squeeze more in. Skipped chips stay next in line for the next row.
 *
 * Pure function so the layout math is unit-testable; the component feeds it
 * real measurements (and falls back to count-slicing when layout isn't
 * available — tests, SSR).
 *
 * A chip wider than the container is clamped to it (mirrors the chip's
 * `max-w-full` + truncate) and occupies a full row.
 */
export function packPages(
  widths: number[],
  containerWidth: number,
  { gap = 6, rowsPerPage = 3 }: { gap?: number; rowsPerPage?: number } = {}
): number[][] {
  const pages: number[][] = [];
  let page: number[] = [];
  let row = 1;
  let x = 0;

  const remaining = widths.map((_, i) => i);
  const chipWidth = (i: number) => Math.min(widths[i] ?? 0, containerWidth);

  while (remaining.length > 0) {
    const leftover = x === 0 ? containerWidth : containerWidth - x - gap;
    const pos = remaining.findIndex((i) => chipWidth(i) <= leftover);

    if (pos === -1) {
      // Nothing left fits this row — start the next (an empty row always
      // fits the clamped width, so this can't loop).
      row += 1;
      x = 0;
      if (row > rowsPerPage) {
        pages.push(page);
        page = [];
        row = 1;
      }
      continue;
    }

    const taken = remaining.splice(pos, 1)[0]!;
    const w = chipWidth(taken);
    page.push(taken);
    x = x === 0 ? w : x + gap + w;
  }

  if (page.length) pages.push(page);
  return pages;
}
